import { cn } from '@perch/ui-tokens';
import { getCityName } from '../../lib/airports';

interface RouteDisplayProps {
  segments: Array<{ origin: string; destination: string }>;
  tripType?: 'one_way' | 'round_trip' | 'multi_city';
  size?: 'sm' | 'md' | 'lg';
  /** Show city names as primary labels with IATA codes as subtitles (md/lg only). */
  showCityNames?: boolean;
  className?: string;
}

const codeClasses = 'font-mono font-semibold tracking-[0.03em] uppercase';

const sizeConfig = {
  sm: { code: 'text-sm', gap: 'gap-1.5' },
  md: { code: 'text-lg', gap: 'gap-3' },
  lg: { code: 'text-2xl', gap: 'gap-4' },
} as const;

/** Build accessible route description for screen readers. */
function buildAriaLabel(
  segments: Array<{ origin: string; destination: string }>,
  tripType: string,
): string {
  if (segments.length === 0) return 'Flight route';

  const formatAirport = (code: string) => {
    const city = getCityName(code);
    return city ? `${city} (${code})` : code;
  };

  const origin = segments[0].origin;
  const destination = segments[segments.length - 1].destination;

  if (tripType === 'round_trip') {
    return `Round trip from ${formatAirport(origin)} to ${formatAirport(destination)}`;
  }

  if (tripType === 'multi_city' && segments.length > 1) {
    const airports: string[] = [];
    for (const seg of segments) {
      if (airports.length === 0 || airports[airports.length - 1] !== seg.origin) {
        airports.push(seg.origin);
      }
      airports.push(seg.destination);
    }
    return `Multi-city route: ${airports.map(formatAirport).join(' to ')}`;
  }

  return `Flight from ${formatAirport(origin)} to ${formatAirport(destination)}`;
}

/** Airport label: city name as primary text, IATA code as subtitle (when showCity is true). */
function AirportLabel({
  code,
  size,
  showCity,
}: {
  code: string;
  size: 'md' | 'lg';
  showCity?: boolean;
}) {
  const city = showCity ? getCityName(code) : undefined;
  const cfg = sizeConfig[size];

  if (!city) {
    return (
      <span className={cn(codeClasses, cfg.code, 'text-stone-900 dark:text-stone-100')}>
        {code}
      </span>
    );
  }

  return (
    <div className="flex flex-col">
      <span
        className={cn(
          'font-sans font-medium text-stone-900 dark:text-stone-100 leading-tight',
          size === 'lg' ? 'text-xl' : 'text-base',
        )}
      >
        {city}
      </span>
      <span
        className={cn(
          codeClasses,
          'text-stone-400 dark:text-stone-500 leading-tight',
          size === 'lg' ? 'text-xs mt-0.5' : 'text-[11px] mt-px',
        )}
      >
        {code}
      </span>
    </div>
  );
}

/** Roundtrip connector: two parallel dashed flight paths, each with a coral ✈ plane. */
function RoundtripConnector({ size }: { size: 'md' | 'lg' }) {
  return (
    <div
      className={cn(
        'flex-1 flex flex-col justify-center',
        size === 'lg' ? 'gap-4' : 'gap-3',
      )}
      aria-hidden="true"
    >
      <div className="roundtrip-line" />
      <div className="roundtrip-line return" />
    </div>
  );
}

/** Inline one-way route: code + route-line + code. */
function OneWayRoute({
  origin,
  destination,
  size,
  showCity,
}: {
  origin: string;
  destination: string;
  size: 'sm' | 'md' | 'lg';
  showCity?: boolean;
}) {
  const cfg = sizeConfig[size];

  if (size === 'sm') {
    return (
      <span className={cn('inline-flex items-center', cfg.gap)}>
        <span className={cn(codeClasses, cfg.code, 'text-stone-900 dark:text-stone-100')}>
          {origin}
        </span>
        <span className="text-stone-400 dark:text-stone-500" aria-hidden="true">
          →
        </span>
        <span className={cn(codeClasses, cfg.code, 'text-stone-900 dark:text-stone-100')}>
          {destination}
        </span>
      </span>
    );
  }

  return (
    <div className={cn('flex items-center', cfg.gap)}>
      <AirportLabel code={origin} size={size} showCity={showCity} />
      <div className="route-line" aria-hidden="true" />
      <AirportLabel code={destination} size={size} showCity={showCity} />
    </div>
  );
}

/** Roundtrip route: city labels + dual dashed flight paths with coral arrows. */
function RoundtripRoute({
  origin,
  destination,
  size,
  showCity,
}: {
  origin: string;
  destination: string;
  size: 'sm' | 'md' | 'lg';
  showCity?: boolean;
}) {
  const cfg = sizeConfig[size];

  if (size === 'sm') {
    return (
      <span className={cn('inline-flex items-center', cfg.gap)}>
        <span className={cn(codeClasses, cfg.code, 'text-stone-900 dark:text-stone-100')}>
          {origin}
        </span>
        <span className="text-stone-400 dark:text-stone-500" aria-hidden="true">
          ↔
        </span>
        <span className={cn(codeClasses, cfg.code, 'text-stone-900 dark:text-stone-100')}>
          {destination}
        </span>
      </span>
    );
  }

  return (
    <div className={cn('flex items-center', cfg.gap)}>
      <AirportLabel code={origin} size={size} showCity={showCity} />
      <RoundtripConnector size={size} />
      <AirportLabel code={destination} size={size} showCity={showCity} />
    </div>
  );
}

/** Multi-city route: route-lines with ✈ between airports, abbreviated when 4+ stops. */
function MultiCityRoute({
  segments,
  size,
  showCity,
}: {
  segments: Array<{ origin: string; destination: string }>;
  size: 'sm' | 'md' | 'lg';
  showCity?: boolean;
}) {
  const cfg = sizeConfig[size];

  // Build ordered list of unique airport codes
  const airports: string[] = [];
  for (const seg of segments) {
    if (airports.length === 0 || airports[airports.length - 1] !== seg.origin) {
      airports.push(seg.origin);
    }
    airports.push(seg.destination);
  }

  if (size === 'sm') {
    return (
      <span className={cn('inline-flex items-center', cfg.gap)}>
        {airports.map((code, i) => (
          <span key={`${code}-${i}`} className="inline-flex items-center gap-1.5">
            <span className={cn(codeClasses, cfg.code, 'text-stone-900 dark:text-stone-100')}>
              {code}
            </span>
            {i < airports.length - 1 && (
              <span className="text-stone-400 dark:text-stone-500" aria-hidden="true">
                →
              </span>
            )}
          </span>
        ))}
      </span>
    );
  }

  // md/lg with 4+ airports: abbreviated — first ✈ [N stops] ✈ last
  if (airports.length > 3) {
    const intermediateCount = airports.length - 2;
    const intermediateCodes = airports.slice(1, -1);
    return (
      <div className={cn('flex items-center', cfg.gap)}>
        <AirportLabel code={airports[0]} size={size} showCity={showCity} />
        <div className="route-line" aria-hidden="true" />
        <span
          className={cn(
            'flex-shrink-0 px-2.5 py-1 rounded-full',
            'text-[10px] uppercase tracking-widest font-semibold',
            'text-stone-500 dark:text-stone-400',
            'bg-stone-100 dark:bg-stone-800',
          )}
          title={intermediateCodes.join(' → ')}
        >
          {intermediateCount} {intermediateCount === 1 ? 'stop' : 'stops'}
        </span>
        <div className="route-line" aria-hidden="true" />
        <AirportLabel code={airports[airports.length - 1]} size={size} showCity={showCity} />
      </div>
    );
  }

  // md/lg with 2-3 airports: full route-lines with ✈ between each pair
  const elements: React.JSX.Element[] = [];
  for (let i = 0; i < airports.length; i++) {
    if (i > 0) {
      elements.push(<div key={`line-${i}`} className="route-line" aria-hidden="true" />);
    }
    elements.push(
      <AirportLabel key={`ap-${airports[i]}-${i}`} code={airports[i]} size={size} showCity={showCity} />,
    );
  }

  return (
    <div className={cn('flex items-center', cfg.gap)}>
      {elements}
    </div>
  );
}

/**
 * Displays a flight route with consistent formatting across the app.
 *
 * - One-way: `SFO → NRT` with dotted route-line and plane marker (md/lg)
 * - Roundtrip: `SFO ↔ NRT` text (sm) or dual dashed flight paths with coral arrows (md/lg)
 * - Multi-city: `SFO → ORD → NRT` text (sm) or chained route-lines with waypoint dots (md/lg)
 *
 * When `showCityNames` is true (md/lg only), displays city names as primary labels
 * with IATA codes as subtitles for improved readability and accessibility.
 */
export function RouteDisplay({
  segments,
  tripType = 'one_way',
  size = 'md',
  showCityNames = false,
  className,
}: RouteDisplayProps) {
  if (segments.length === 0) return null;

  const origin = segments[0].origin;
  const destination = segments[segments.length - 1].destination;
  const showCity = showCityNames && size !== 'sm';

  return (
    <div
      className={cn('flex items-center', className)}
      role="group"
      aria-label={buildAriaLabel(segments, tripType)}
    >
      {tripType === 'round_trip' ? (
        <RoundtripRoute origin={origin} destination={destination} size={size} showCity={showCity} />
      ) : tripType === 'multi_city' && segments.length > 1 ? (
        <MultiCityRoute segments={segments} size={size} showCity={showCity} />
      ) : (
        <OneWayRoute origin={origin} destination={destination} size={size} showCity={showCity} />
      )}
    </div>
  );
}
